import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';

interface BarcodeScannerProps {
  onScan: (code: string) => void;
  onClose: () => void;
  continuousMode?: boolean;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onScan,
  onClose,
  continuousMode = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const [error, setError] = useState<string>('');
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  // THE CAMERA MUST START ONCE AND STAY STARTED.
  //
  // These three were state or raw props read inside the zxing decode callback,
  // and every one of them was in the effect's dependency array. So a successful
  // scan called setLastScanned -> the effect re-ran -> codeReader.reset(), every
  // track stopped, getUserMedia called again. The camera restarted on every
  // single scan. Call sites also pass onClose as an inline arrow, so it also
  // restarted on any unrelated parent render.
  //
  // That is not a cosmetic stutter. A restarted camera loses its autofocus lock
  // and its exposure adaptation, which is exactly what you cannot afford when
  // someone is holding a phone over a feed bag in a dim garage - the conditions
  // where the first read already takes a few seconds.
  //
  // Refs hold the current value without participating in the dependency array,
  // so the callback always sees fresh values and the effect never re-runs.
  const lastScannedRef = useRef<string>('');
  // Display only, for the continuous-mode readout. Safe to be state now that
  // the effect's dependency array is empty - setting it cannot restart the
  // camera, which is precisely what it used to do.
  const [lastScannedLabel, setLastScannedLabel] = useState('');
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);
  const continuousRef = useRef(continuousMode);

  useEffect(() => {
    onScanRef.current = onScan;
    onCloseRef.current = onClose;
    continuousRef.current = continuousMode;
  }, [onScan, onClose, continuousMode]);

  useEffect(() => {
    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;

    const startScanning = async () => {
      try {
        // Stop any existing streams first
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Request camera permission - use 'ideal' instead of exact to allow fallback
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }
        });
        streamRef.current = stream;

        // Check for flash/torch capability
        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities?.() as any;
        if (capabilities?.torch) {
          setHasFlash(true);
        }

        // Start decoding
        await codeReader.decodeFromVideoDevice(
          null,
          videoRef.current!,
          (result, err) => {
            if (result) {
              const code = result.getText();

              // Avoid duplicate scans
              if (code === lastScannedRef.current) return;
              lastScannedRef.current = code;
              setLastScannedLabel(code);

              // Vibrate on success
              if (navigator.vibrate) {
                navigator.vibrate(200);
              }

              // Play beep sound using AudioContext
              try {
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                const oscillator = audioCtx.createOscillator();
                const gainNode = audioCtx.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioCtx.destination);
                oscillator.frequency.value = 1000;
                oscillator.type = 'sine';
                gainNode.gain.value = 0.3;
                oscillator.start();
                setTimeout(() => {
                  oscillator.stop();
                  audioCtx.close();
                }, 150);
              } catch (e) {
                // Audio not supported, continue silently
              }

              onScanRef.current(code);

              if (!continuousRef.current) {
                // Close scanner after successful scan
                setTimeout(() => onCloseRef.current(), 300);
              } else {
                // Reset for next scan after a delay
                setTimeout(() => {
                  lastScannedRef.current = '';
                  setLastScannedLabel('');
                }, 1500);
              }
            }
            if (err && !(err instanceof NotFoundException)) {
              console.error('Scan error:', err);
            }
          }
        );
      } catch (err: any) {
        console.error('Camera error:', err);
        if (err.name === 'NotAllowedError') {
          setError('Camera permission denied. Please allow camera access to scan barcodes.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found. Please use a device with a camera.');
        } else {
          setError('Failed to access camera. Please try again.');
        }
      }
    };

    startScanning();

    return () => {
      codeReader.reset();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
    // EMPTY ON PURPOSE. Mount once, tear down once. Everything the callback
    // needs is behind a ref (above), so nothing here can restart the camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFlash = async () => {
    if (!streamRef.current) return;

    const track = streamRef.current.getVideoTracks()[0];
    try {
      await track.applyConstraints({
        advanced: [{ torch: !flashOn } as any]
      });
      setFlashOn(!flashOn);
    } catch (e) {
      console.error('Failed to toggle flash:', e);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {error ? (
          <div style={styles.errorContainer}>
            <p style={styles.errorText}>{error}</p>
            <button onClick={onClose} style={styles.closeButton}>
              Close
            </button>
          </div>
        ) : (
          <>
            <p style={styles.instruction}>Point camera at barcode</p>

            <div style={styles.videoContainer}>
              <video
                ref={videoRef}
                style={styles.video}
                playsInline
                autoPlay
                muted
              />
              <div style={styles.viewfinder}>
                <div style={styles.viewfinderCorner} />
              </div>
              {lastScannedLabel && continuousMode && (
                <div style={styles.scannedBadge}>
                  Scanned: {lastScannedLabel}
                </div>
              )}
            </div>

            <div style={styles.controls}>
              {hasFlash && (
                <button
                  onClick={toggleFlash}
                  style={{
                    ...styles.flashButton,
                    backgroundColor: flashOn ? '#FFC107' : 'rgba(255,255,255,0.2)'
                  }}
                >
                  {flashOn ? 'Flash ON' : 'Flash OFF'}
                </button>
              )}

              <button onClick={onClose} style={styles.cancelButton}>
                {continuousMode ? 'Done Scanning' : 'Cancel'}
              </button>
            </div>

            {continuousMode && (
              <p style={styles.continuousTip}>
                Continuous mode: Keep scanning multiple products
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.95)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: '100%',
    maxWidth: '500px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  instruction: {
    color: '#fff',
    fontSize: '18px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  videoContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '4/3',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  viewfinder: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    height: '40%',
    border: '2px solid rgba(255,255,255,0.8)',
    borderRadius: '8px',
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
  },
  viewfinderCorner: {
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    width: '20px',
    height: '20px',
    borderTop: '4px solid #00ff00',
    borderLeft: '4px solid #00ff00',
  },
  scannedBadge: {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#4CAF50',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    justifyContent: 'center',
  },
  flashButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#fff',
  },
  cancelButton: {
    padding: '12px 32px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  continuousTip: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '14px',
    marginTop: '16px',
    textAlign: 'center',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '16px',
    marginBottom: '20px',
  },
  closeButton: {
    padding: '12px 32px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default BarcodeScanner;
